import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { paymentApi } from '../api/payment.api';
import type { PaymentFilters } from '../types';

export const usePayments = (filters?: PaymentFilters) => {
  const queryClient = useQueryClient();

  // Fetch payments
  const paymentsQuery = useQuery({
    queryKey: ['payments', filters],
    queryFn: () => paymentApi.fetchPayments(filters),
    staleTime: 30000, // 30 seconds
  });

  // Update payment affaire_id
  const updateAffaireMutation = useMutation({
    mutationFn: paymentApi.updatePaymentAffaire,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
    }
  });

  // Validate payment
  const validatePaymentMutation = useMutation({
    mutationFn: paymentApi.validatePayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
    }
  });

  // Ignore/unignore payment
  const ignorePaymentMutation = useMutation({
    mutationFn: ({ paymentId, isIgnored }: { paymentId: number; isIgnored: boolean }) => 
      isIgnored 
        ? paymentApi.ignorePayment(paymentId)  // If we want to ignore, call ignore
        : paymentApi.unignorePayment(paymentId), // If we want to unignore, call unignore (DELETE)
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
    }
  });

  return {
    payments: paymentsQuery.data || [],
    isLoading: paymentsQuery.isLoading,
    error: paymentsQuery.error,
    refetch: paymentsQuery.refetch,
    updateAffaire: updateAffaireMutation.mutate,
    validatePayment: validatePaymentMutation.mutate,
    ignorePayment: ignorePaymentMutation.mutate,
    isUpdating: updateAffaireMutation.isPending,
    isValidating: validatePaymentMutation.isPending,
    isIgnoring: ignorePaymentMutation.isPending,
    updateError: updateAffaireMutation.error,
    validateError: validatePaymentMutation.error,
    ignoreError: ignorePaymentMutation.error,
  };
};